import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of, from } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';
import { StepTask } from '@telespot/domain';
import { LabelStore } from './label.store';
import { PIPELINE_SERVICE, type PipelineRepository } from '@telespot/protocols/domain';

export interface Step {
  id: string;
  stepTask: StepTask;
  pipelineId: string;
  generateFinding: string; // TODO: define
  trigger: string; // TODO: define
  order: number;
  executorId: string;
  thresholds: Record<string, number>;
  allowNewOptions: boolean;
  enableMosaicView: boolean;
  counter: number;
  categories: string[];
  multiple: boolean;
}

export interface CategorizationItem {
  id: string;
  stepId: string;
  options: string[];
}

export interface StepState {
  steps: Step[];
  pipelineCategories: Record<string, string[]>;
  pipelineSteps: Record<string, string[]>;
  categories: Record<string, CategorizationItem>;
  loading: boolean;
  error: string | undefined;
}

const initialState: StepState = {
  steps: [],
  pipelineCategories: {},
  pipelineSteps: {},
  categories: {},
  loading: true,
  error: undefined,
};

@Injectable()
export class StepStore extends ComponentStore<StepState> {
  private readonly pipelineRepo = inject<PipelineRepository>(PIPELINE_SERVICE);
  private readonly labelStore = inject(LabelStore);

  constructor() {
    super(initialState);
  }

  // #region Selectors

  public readonly steps$ = this.select((state) => state.steps);
  public readonly pipelineCategories$ = this.select((state) => state.pipelineCategories);
  public readonly pipelineSteps$ = this.select((state) => state.pipelineSteps);
  public readonly categories$ = this.select((state) => state.categories);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);

  // #endregion

  // #region Updaters

  public readonly setSteps = this.updater((state, steps: Step[]) => ({
    ...state,
    steps,
  }));

  public readonly setPipelineCategories = this.updater((state, pipelineCategories: Record<string, string[]>) => ({
    ...state,
    pipelineCategories,
  }));

  public readonly setPipelineSteps = this.updater((state, pipelineSteps: Record<string, string[]>) => ({
    ...state,
    pipelineSteps,
  }));

  public readonly setCategories = this.updater((state, categories: Record<string, CategorizationItem>) => ({
    ...state,
    categories,
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    error,
  }));

  public readonly clearError = this.updater((state) => ({
    ...state,
    error: undefined,
  }));

  // #endregion

  // #region Effects

  public readonly findForSample = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      tap(() => {
        this.setLoading(true);
        this.clearError();
      }),
      switchMap((sampleId: string) => {
        return from(this.pipelineRepo.findStepsForSample(sampleId)).pipe(
          tap((data: { steps: Step[]; categories: Record<string, CategorizationItem> }) => {
            // Order steps by their `order` property so the protocol panel renders
            // steps/categories in the intended sequence (the fetch order isn't
            // guaranteed). Grouping by pipeline below preserves this relative order.
            const orderedSteps = [...data.steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            this.setSteps(orderedSteps);
            this.setCategories(data.categories);

            const steps = orderedSteps.reduce(
              (acc, step) => ({
                ...acc,
                [step.pipelineId]: [...(acc[step.pipelineId] || []), step.id],
              }),
              {} as Record<string, string[]>
            );

            const pipelineCategories = orderedSteps.reduce(
              (acc, step) => ({
                ...acc,
                [step.pipelineId]: [...(acc[step.pipelineId] || []), ...step.categories],
              }),
              {} as Record<string, string[]>
            );

            this.setPipelineSteps(steps);
            this.setPipelineCategories(pipelineCategories);

            this.setLoading(false);
            this.clearError();
          }),
          tap(({ steps, categories }) => {
            const uuids: string[] = [];

            for (const step of steps) {
              for (const category of step.categories) {
                uuids.push(category);
                uuids.push(...(categories[category]?.options ?? []));
              }
            }
            this.labelStore.fetchLabelsByUUID(uuids);
          }),
          catchError((error: string) => {
            this.setError(error);
            this.setLoading(false);
            return of(null);
          })
        );
      })
    )
  );

  // #endregion
}
